import { ref } from 'vue';

// Default delivery and pickup configuration values
const defaultConfig = {
    deliveryLabel: 'Delivery',
    pickupLabel: 'Pickup',
    deliveryHeading: 'Enter your address to check delivery availability:',
    pickupHeading: 'Nearest Centre with Stock:',
    deliveryEnabled: true,
    pickupEnabled: true
};

const deliveryConfig = ref(null);

// Function to check if the stored config is expired (older than 1 hour)
function isConfigExpired(timestamp) {
    if (!timestamp) return true;

    const now = new Date().getTime();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    return (now - timestamp) > oneHour;
}

// Function to get delivery config from session storage or API
async function getDeliveryConfig() {
    try {
        // If we already have the config in memory, use it
        if (deliveryConfig.value) {
            return deliveryConfig.value;
        }

        // Check session storage first
        const storedConfig = sessionStorage.getItem('ec_delivery_config');

        if (storedConfig) {
            const { data, timestamp } = JSON.parse(storedConfig);

            // If the config is not expired, use it
            if (!isConfigExpired(timestamp)) {
                deliveryConfig.value = data;
                return data;
            }
        }

        // If no stored config or it's expired, fetch from API
        const response = await fetch('/api/delivery-config');
        if (!response.ok) {
            throw new Error('Failed to fetch delivery config');
        }

        const data = await response.json();

        data.deliveryEnabled = Boolean(data.deliveryEnabled === true || data.deliveryEnabled === 1 || data.deliveryEnabled === "1");
        data.pickupEnabled = Boolean(data.pickupEnabled === true || data.pickupEnabled === 1 || data.pickupEnabled === "1");
        data.freeShippingEnabled = Boolean(data.freeShippingEnabled === true || data.freeShippingEnabled === 1 || data.freeShippingEnabled === "1");
        data.freeShippingThreshold = (data.freeShippingThreshold !== null && data.freeShippingThreshold !== undefined && data.freeShippingThreshold !== '')
            ? parseFloat(data.freeShippingThreshold)
            : null;
        data.freeShippingBasis = data.freeShippingBasis === 'post_discount' ? 'post_discount' : 'pre_discount';

        // Store in session storage with timestamp
        sessionStorage.setItem('ec_delivery_config', JSON.stringify({
            data,
            timestamp: new Date().getTime()
        }));

        // Store in memory
        deliveryConfig.value = data;

        return data;
    } catch (error) {
        console.error('Error getting delivery config:', error);
        // Return default values if API call fails
        return { ...defaultConfig };
    }
}

// Initialize delivery config when the site loads
if (typeof window !== 'undefined') {
    // Call getDeliveryConfig immediately to load the config
    getDeliveryConfig().catch(error => {
        console.error('Failed to load delivery config:', error)
    });
}

export function useDeliveryConfig() {
    return {
        deliveryConfig,
        getDeliveryConfig
    };
}
